import React, { useState } from 'react';
import { useLocalizedText } from '../../../hooks/localizedText';
import { useQueryClient } from '@tanstack/react-query';

import Multiselect, { MultiselectProps } from '@amzn/awsui-components-react/polaris/multiselect';

import { getOptionFromMultiValues } from '../../../utils/url';
import { useSearchParams } from 'react-router-dom';
import { useFeatures, getEnabledFeatures } from '../../../hooks/features';

import { usePaginatedRedVelvetQuery } from '../../../hooks/paginatedRedVelvetQuery';
import { searchPeople } from '../../../utils/papiSearch';

export interface AmbassadorSelectProps {
  currentAmbassadors: string;
  onChange: NonNullable<MultiselectProps['onChange']>;
  testId?: string;
  placeholder?: string;
}

export function AmbassadorSelect({
  currentAmbassadors,
  onChange,
  testId,
  placeholder,
}: AmbassadorSelectProps) {
  const namespaces = ['attendance', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const queryClient = useQueryClient();

  const [people, setPeople] = useState<MultiselectProps.Options>([]);
  const [selectedPeople, setSelectedPeople] = useState<MultiselectProps.Options>([]);
  const [loading, setLoading] = useState(false);

  // grabbing just alias for calling PAPI ['alias-abcde-employeeId-12345678']
  const currentAliases =
    currentAmbassadors && currentAmbassadors !== '[]'
      ? currentAmbassadors
          .slice(1, -1)
          .split(',')
          .map((amb) => amb.split('-')[1])
      : [];
  // Load people data from currentAmbassadors that might be in url
  const { queryResult } = usePaginatedRedVelvetQuery({
    redVelvetQueryKey: ['getPeople', { people: `[${currentAliases.join(',')}]` }],
  });

  // We CANNOT use the usual usePaginatedRedVelvetQuery, as the filteringText does not play nicely with useState
  // Luckily, the Multiselect component handles retries
  const loadPeople = async (filteringText: string) => {
    setLoading(true);
    try {
      const people = await searchPeople(
        filteringText,
        queryClient,
        getEnabledFeatures(features, searchParams)
      );
      if (people === null) {
        setPeople([]);
        return;
      }
      setPeople(
        people.map((person) => ({
          label: person.lastName + ', ' + person.firstName,
          description: person.alias,
          value: `alias-${person.alias}-employeeId-${person.employeeId}`,
        }))
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Multiselect
      selectedOptions={getOptionFromMultiValues(currentAmbassadors, [
        // Check the currently searched data, the pre-loaded data from url string,
        // and any saved data from previous searches for a match
        ...people,
        ...(queryResult.data?.people || []).map((person) => ({
          label: person.lastName + ', ' + person.firstName,
          description: person.alias,
          value: `alias-${person.alias}-employeeId-${person.employeeId}`, // TODO: investigate a long term storage solution cce-5397
        })),
        ...selectedPeople,
      ])}
      options={people}
      onChange={(event) => {
        // We need to save any Person data for selected users, so it does not vanish for a split second
        // when we update the list of selected user aliases, or when we change filter text
        setSelectedPeople(event.detail.selectedOptions);
        onChange(event);
      }}
      onLoadItems={({ detail }) => {
        loadPeople(detail.filteringText);
      }}
      statusType={loading ? 'loading' : undefined}
      filteringType="manual"
      keepOpen={false}
      placeholder={placeholder || t('additionalInfo.searchAmbassadors')}
      data-testid={testId}
    />
  );
}
